import React, { useRef, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Pencil, 
  Eraser, 
  RotateCcw, 
  Download, 
  Upload,
  Palette
} from 'lucide-react';

const PatternDrawing = ({ onPatternChange, initialPattern = null }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState('pen'); // 'pen' or 'eraser'
  const [strokeColor, setStrokeColor] = useState('#2563eb');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [canvasScale, setCanvasScale] = useState(1);
  const [showGrid, setShowGrid] = useState(false);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      // Set canvas size
      canvas.width = 400;
      canvas.height = 300;
      
      // Set white background and draw grid if enabled
      redrawCanvas();
      
      // Load initial pattern if provided
      if (initialPattern) {
        loadPatternFromString(initialPattern);
      }
    }
  }, [showGrid]);

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear and set white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw grid if enabled
    if (showGrid) {
      ctx.strokeStyle = '#f0f0f0';
      ctx.lineWidth = 1;
      ctx.globalCompositeOperation = 'source-over';
      
      // Vertical lines
      for (let x = 0; x <= canvas.width; x += 20) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      // Horizontal lines
      for (let y = 0; y <= canvas.height; y += 20) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    }
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate scale factors to handle canvas display size vs actual size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const coords = getCanvasCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const coords = getCanvasCoordinates(e);
    
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    if (currentTool === 'pen') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = strokeColor;
    } else if (currentTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = strokeWidth * 2; // Eraser is wider
    }
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      savePattern();
    }
  };

  // Touch events for mobile support - Fixed coordinates
  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const coords = getCanvasCoordinates({
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const handleTouchMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const coords = getCanvasCoordinates({
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    if (currentTool === 'pen') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = strokeColor;
    } else if (currentTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = strokeWidth * 2;
    }
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    stopDrawing();
  };

  const clearCanvas = () => {
    redrawCanvas();
    savePattern();
  };

  const toggleGrid = () => {
    setShowGrid(!showGrid);
  };

  const zoomIn = () => {
    setCanvasScale(prev => Math.min(prev + 0.2, 2));
  };

  const zoomOut = () => {
    setCanvasScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const resetZoom = () => {
    setCanvasScale(1);
  };

  const savePattern = () => {
    const canvas = canvasRef.current;
    if (canvas && onPatternChange) {
      // Convert canvas to base64 string
      const dataUrl = canvas.toDataURL('image/png');
      onPatternChange(dataUrl);
    }
  };

  const loadPatternFromString = (patternData) => {
    if (!patternData || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    
    img.src = patternData;
  };

  const downloadPattern = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = 'pattern_schema.png';
    link.href = canvas.toDataURL();
    link.click();
  };

  const uploadPattern = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        loadPatternFromString(event.target.result);
        savePattern();
      };
      reader.readAsDataURL(file);
    }
  };

  const colors = [
    '#2563eb', '#dc2626', '#16a34a', '#ca8a04', 
    '#9333ea', '#c2410c', '#0891b2', '#be123c'
  ];

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Pencil className="w-5 h-5" />
          <span>Schéma de déverrouillage</span>
        </CardTitle>
        <CardDescription>
          Dessinez le schéma de déverrouillage de l'appareil
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Canvas */}
        <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            className={`cursor-crosshair block ${currentTool === 'eraser' ? 'cursor-grab' : 'cursor-crosshair'}`}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ 
              display: 'block', 
              width: '100%', 
              height: 'auto',
              transform: `scale(${canvasScale})`,
              transformOrigin: 'top left',
              imageRendering: 'pixelated'
            }}
          />
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
          <div className="flex items-center space-x-2">
            <button
              onClick={zoomOut}
              className="w-8 h-8 bg-white border border-slate-200 rounded hover:bg-slate-50 flex items-center justify-center"
              title="Zoom arrière"
            >
              <span className="text-lg font-bold">-</span>
            </button>
            <span className="text-sm font-medium text-slate-600 min-w-[60px] text-center">
              {Math.round(canvasScale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              className="w-8 h-8 bg-white border border-slate-200 rounded hover:bg-slate-50 flex items-center justify-center"
              title="Zoom avant"
            >
              <span className="text-lg font-bold">+</span>
            </button>
            <button
              onClick={resetZoom}
              className="px-3 py-1 bg-white border border-slate-200 rounded text-xs hover:bg-slate-50"
              title="Zoom 100%"
            >
              Reset
            </button>
          </div>
          
          <button
            onClick={toggleGrid}
            className={`px-3 py-1 text-xs rounded border ${
              showGrid 
                ? 'bg-blue-100 border-blue-200 text-blue-700' 
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            title="Afficher/masquer la grille"
          >
            Grille
          </button>
        </div>

        {/* Tools */}
        <div className="space-y-4">
          {/* Tool Selection */}
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant={currentTool === 'pen' ? 'default' : 'outline'}
              onClick={() => setCurrentTool('pen')}
              className="flex-1"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Stylo
            </Button>
            <Button
              size="sm"
              variant={currentTool === 'eraser' ? 'default' : 'outline'}
              onClick={() => setCurrentTool('eraser')}
              className="flex-1"
            >
              <Eraser className="w-4 h-4 mr-2" />
              Gomme
            </Button>
          </div>

          {/* Color Palette */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700 flex items-center">
              <Palette className="w-4 h-4 mr-2" />
              Couleur
            </p>
            <div className="grid grid-cols-8 gap-2">
              {colors.map((color) => (
                <button
                  key={color}
                  className={`w-8 h-8 rounded-full border-2 ${
                    strokeColor === color ? 'border-slate-400' : 'border-slate-200'
                  } hover:border-slate-400 transition-colors`}
                  style={{ backgroundColor: color }}
                  onClick={() => setStrokeColor(color)}
                />
              ))}
            </div>
          </div>

          {/* Stroke Width */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">
              Épaisseur: {strokeWidth}px
            </p>
            <input
              type="range"
              min="1"
              max="10"
              value={strokeWidth}
              onChange={(e) => setStrokeWidth(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={clearCanvas}
              className="flex items-center"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Effacer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadPattern}
              className="flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Sauver
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <label className="block">
              <Button
                size="sm"
                variant="outline"
                className="w-full cursor-pointer"
                asChild
              >
                <div className="flex items-center">
                  <Upload className="w-4 h-4 mr-2" />
                  Charger image
                </div>
              </Button>
              <input
                type="file"
                accept="image/*"
                onChange={uploadPattern}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Instructions */}
        <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
          <p className="font-medium mb-1">Instructions:</p>
          <ul className="space-y-1">
            <li>• Dessinez le schéma avec votre souris/doigt</li>
            <li>• Utilisez la gomme pour corriger</li>
            <li>• Changez couleur et épaisseur selon besoin</li>
            <li>• Le schéma est sauvegardé automatiquement</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default PatternDrawing;